import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Factory, Cog, BookOpen, Users, Briefcase, MessageSquare, MapPin, Layers } from "lucide-react";

const ALL_LINKS = {
  manufacturing: {
    title: "Manufacturing",
    description: "Our precision automated machinery production unit in Navi Mumbai",
    path: "/manufacturing",
    icon: Factory,
  },
  process: {
    title: "Our 9-Step Process",
    description: "Systematic execution from design to handover",
    path: "/process",
    icon: Cog,
  },
  solutions: {
    title: "Industry Solutions",
    description: "Workspace solutions for every sector",
    path: "/solutions",
    icon: Layers,
  },
  caseStudies: {
    title: "Case Studies",
    description: "Real projects delivered across India",
    path: "/case-studies",
    icon: BookOpen,
  },
  testimonials: {
    title: "Client Testimonials",
    description: "Hear from a few of our repute satisfied corporates",
    path: "/testimonials",
    icon: MessageSquare,
  },
  products: {
    title: "Product Catalogue",
    description: "Workstations, chairs, tables & more",
    path: "/products/linear-workstations",
    icon: Briefcase,
  },
  partners: {
    title: "Partner With Us",
    description: "Architects & consultant partnerships",
    path: "/partners",
    icon: Users,
  },
  contact: {
    title: "Get In Touch",
    description: "Request a consultation or factory visit",
    path: "/contact",
    icon: MapPin,
  },
};

const PAGE_LINK_MAP = {
  manufacturing: ["process", "solutions", "caseStudies", "testimonials", "partners", "contact"],
  process: ["manufacturing", "solutions", "caseStudies", "testimonials", "partners", "contact"],
  solutions: ["manufacturing", "process", "caseStudies", "testimonials", "partners", "contact"],
  caseStudies: ["solutions", "manufacturing", "process", "testimonials", "partners", "contact"],
  testimonials: ["caseStudies", "solutions", "manufacturing", "process", "partners", "contact"],
  partners: ["manufacturing", "process", "solutions", "caseStudies", "testimonials", "contact"],
  distributors: ["manufacturing", "solutions", "caseStudies", "process", "testimonials", "contact"],
  products: ["solutions", "manufacturing", "caseStudies", "process", "testimonials", "contact"],
  locations: ["solutions", "manufacturing", "caseStudies", "process", "testimonials", "contact"],
  contact: ["solutions", "manufacturing", "caseStudies", "process", "testimonials", "partners"],
  blog: ["solutions", "caseStudies", "manufacturing", "process", "testimonials", "contact"],
  clientele: ["testimonials", "caseStudies", "process", "solutions", "partners", "contact"],
};

export default function ExploreMore({ currentPage = "manufacturing", maxLinks = 6 }) {
  const linkKeys = (PAGE_LINK_MAP[currentPage] || PAGE_LINK_MAP.manufacturing).slice(0, maxLinks);

  return (
    <section className="py-20 bg-white border-t border-[#E2E8F0]" data-testid="explore-more-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-3 block">
            Explore Ezeefit
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
            Continue Exploring
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {linkKeys.map((key, index) => {
            const link = ALL_LINKS[key];
            if (!link) return null;
            const Icon = link.icon;
            const isFeatured = currentPage === "clientele" && key === "testimonials";

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
              >
                <Link
                  to={link.path}
                  className={
                    isFeatured
                      ? "group flex items-start gap-4 p-5 border border-[#F97316] bg-[#FFF7ED] hover:bg-[#FFEDD5] transition-all duration-300"
                      : "group flex items-start gap-4 p-5 border border-[#E2E8F0] hover:border-[#F97316] bg-[#FAFBFC] hover:bg-[#FFF7ED] transition-all duration-300"
                  }
                  data-testid={`explore-link-${key}`}
                >
                  <div
                    className={
                      isFeatured
                        ? "w-10 h-10 bg-[#F97316] flex items-center justify-center shrink-0 transition-colors duration-300"
                        : "w-10 h-10 bg-[#0F172A] group-hover:bg-[#F97316] flex items-center justify-center shrink-0 transition-colors duration-300"
                    }
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0F172A] group-hover:text-[#F97316] transition-colors text-sm">
                      {link.title}
                    </h3>
                    <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">
                      {link.description}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#F97316] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
